import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useLocale } from '../context';
import { api } from '../api';
import {
  IconAccountBets,
  IconAccountBonuses,
  IconAccountCashier,
  IconAccountProfile,
  IconAccountPromos,
  IconAccountSecurity,
  IconAccountSupport,
  IconBellFilled,
  IconInAppGift,
} from './VurgunIcons';
import { pushRoute } from '../navigation';

const PANEL_W = 512;
const PANEL_H = 500;
const PANEL_OFFSET = 5;
const MARK_ANIM_MS = 1000;

export function IconBell() {
  return <IconBellFilled />;
}


function notificationKind(notification) {
  const type = String(notification?.type || '').toLowerCase();
  const text = `${notification?.title || ''} ${notification?.body || ''}`.toLowerCase();
  if (['promo', 'promotion', 'promos', 'action', 'campaign'].includes(type) || /promo|kampanya|turnuva|tournament/.test(text)) return 'promo';
  if (['bonus', 'bonuses', 'freespin', 'free_spin', 'fs', 'gift'].includes(type) || /\b(fs|free\s*spin|freespin|bonus)\b/.test(text)) return 'bonus';
  if (['withdraw', 'withdrawal', 'çekim'].includes(type) || /para çek|çekim|withdraw/.test(text)) return 'withdraw';
  if (['deposit', 'cashier', 'payment', 'payments', 'finance'].includes(type) || /para yatır|yatırım|deposit|ödeme/.test(text)) return 'deposit';
  if (['support', 'ticket', 'help'].includes(type) || /destek|talebiniz|yanıt/.test(text)) return 'support';
  if (['security', 'responsible', 'limit'].includes(type) || /güvenlik|sorumlu|limit/.test(text)) return 'security';
  if (['kyc', 'verification', 'verify', 'profile', 'welcome', 'account'].includes(type) || /doğrula|hoş geld|profil|üyeli/.test(text)) return 'profile';
  if (['bets', 'bet', 'history', 'sports', 'sport'].includes(type) || /bahis|spor/.test(text)) return 'bets';
  if (['casino', 'slots', 'slot'].includes(type) || /casino|slot/.test(text)) return 'casino';
  if (['system', 'info', 'news'].includes(type)) return 'system';
  return type || 'system';
}

function iconForKind(kind) {
  switch (kind) {
    case 'promo':
      return { Icon: IconAccountPromos, tone: 'promo' };
    case 'bonus':
      return { Icon: IconInAppGift, tone: 'bonus' };
    case 'deposit':
    case 'withdraw':
    case 'cashier':
      return { Icon: IconAccountCashier, tone: 'cashier' };
    case 'support':
      return { Icon: IconAccountSupport, tone: 'support' };
    case 'security':
      return { Icon: IconAccountSecurity, tone: 'security' };
    case 'profile':
    case 'welcome':
    case 'kyc':
      return { Icon: IconAccountProfile, tone: 'profile' };
    case 'bets':
    case 'sport':
    case 'sports':
      return { Icon: IconAccountBets, tone: 'bets' };
    case 'casino':
      return { Icon: IconAccountBonuses, tone: 'casino' };
    default:
      return { Icon: IconBellFilled, tone: 'system' };
  }
}


const TR_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

function formatNotificationDate(value, locale) {
  if (!value) return '';
  const date = new Date(value);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  if (locale === 'tr') {
    return `${date.getDate()}\u00a0${TR_MONTHS[date.getMonth()]},\u00a0${hours}:${minutes}`;
  }

  const month = date.toLocaleString('en-GB', { month: 'long' });
  return `${date.getDate()}\u00a0${month},\u00a0${hours}:${minutes}`;
}

export default function NotificationsDropdown({ unread = 0, onUnreadChange, onNavigate }) {
  const { token } = useAuth();
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [markMounted, setMarkMounted] = useState(unread > 0);
  const [markHidden, setMarkHidden] = useState(unread === 0);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const hideTimerRef = useRef(null);
  const pulseTimerRef = useRef(null);
  const prevUnreadRef = useRef(unread);

  const clearPulse = () => {
    if (pulseTimerRef.current) {
      window.clearInterval(pulseTimerRef.current);
      pulseTimerRef.current = null;
    }
  };

  const load = () => {
    if (!token) return;
    api.notifications(token).then((list) => {
      setItems(list.slice(0, 6));
      onUnreadChange?.(list.filter((n) => !n.read).length);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
  }, [token]);

  useEffect(() => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    clearPulse();

    const prevUnread = prevUnreadRef.current;
    prevUnreadRef.current = unread;

    if (unread > 0) {
      setMarkMounted(true);
      if (prevUnread === 0) {
        setMarkHidden(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setMarkHidden(false));
        });
      }
      // Fonbet: toggle unreadMark_hidden every 1s so ::before glow breathes
      pulseTimerRef.current = window.setInterval(() => {
        setMarkHidden((v) => !v);
      }, MARK_ANIM_MS);
      return () => {
        clearPulse();
      };
    }

    if (prevUnread > 0) {
      setMarkHidden(true);
      hideTimerRef.current = window.setTimeout(() => {
        setMarkMounted(false);
        hideTimerRef.current = null;
      }, MARK_ANIM_MS);
      return () => {
        if (hideTimerRef.current) {
          window.clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
      };
    }

    setMarkMounted(false);
    setMarkHidden(true);
    return undefined;
  }, [unread]);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(window.innerWidth - PANEL_W - 8, rect.left + rect.width / 2 - PANEL_W / 2));
    const top = Math.min(window.innerHeight - PANEL_H - 8, rect.bottom + PANEL_OFFSET);
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onLayout = () => updatePosition();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    const onDoc = (e) => {
      const root = rootRef.current;
      const panel = panelRef.current;
      if (root?.contains(e.target) || panel?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const markAllRead = async () => {
    if (!token) return;
    try {
      await api.readAllNotifications(token);
      setItems((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
      onUnreadChange?.(0);
    } catch {
      /* ignore */
    }
    load();
  };

  const toggle = () => {
    setOpen((v) => {
      if (!v) {
        load();
        void markAllRead();
      }
      return !v;
    });
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  const markRead = async (id) => {
    await api.readNotification(token, id);
    load();
  };

  const handleDetails = async (notification) => {
    if (!notification.read) await markRead(notification.id);
    setOpen(false);

    if (notification.action_url) {
      if (notification.action_url.startsWith('http')) {
        window.open(notification.action_url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = notification.action_url;
      }
      return;
    }

    if (notification.type === 'promo') {
      onNavigate?.('promos');
      pushRoute('promos');
      return;
    }

    onNavigate?.('account', { accountSection: 'notifications' });
    pushRoute('account', { accountSection: 'notifications' });
  };

  return (
    <div className="header-item header-notifications" ref={rootRef}>
      <span
        ref={btnRef}
        role="button"
        tabIndex={0}
        className={`header-ctrl header-ctrl--icon in-app-button${open ? ' is-selected' : ''}`}
        aria-label={tr('header.notifications')}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <span className={`in-app-button__icon${open ? ' is-active' : ''}`} aria-hidden="true">
          <IconBell />
        </span>
        {markMounted && (
          <div
            className={`unread-mark mark${markHidden ? ' unread-mark--hidden' : ''}`}
            aria-hidden="true"
          />
        )}
      </span>
      {open && createPortal(
        <div
          ref={panelRef}
          className="in-app-viewer-box"
          style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          role="dialog"
          aria-label={tr('header.notifications')}
        >
          <div className="in-app-header">
            <div className="in-app-header__caption">{tr('header.notifications')}</div>
          </div>
          <div className="in-app-viewer-box__viewer in-app-scroll-box">
            {items.length === 0 ? (
              <div className="in-app-empty">{tr('account.notifications.empty')}</div>
            ) : (
              items.map((n, index) => (
                <article
                  key={n.id}
                  className={`in-app-container in-app-item${index === items.length - 1 ? ' in-app-item--last' : ''}${n.read ? '' : ' is-unread'}`}
                >
                  {(() => {
                    const { Icon, tone } = iconForKind(notificationKind(n));
                    return (
                      <span className={`in-app-container__icon in-app-icon in-app-icon--${tone}`} aria-hidden="true">
                        <Icon />
                      </span>
                    );
                  })()}
                  <div className="in-app-container__content">
                    <div className="in-app-headline">{n.title}</div>
                    <p className="in-app-content in-app-text">{n.body}</p>
                    {n.image_url && (
                      <img
                        className="in-app-image img"
                        src={n.image_url}
                        alt="Picture for notification"
                      />
                    )}
                    <div className="in-app-bottom-bar">
                      <button
                        type="button"
                        className="in-app-bottom-bar__btn"
                        onClick={() => handleDetails(n)}
                      >
                        {n.action_label || tr('account.notifications.details')}
                      </button>
                      <span className="in-app-bottom-bar__spacer" aria-hidden="true" />
                      <span className="in-app-date date">{formatNotificationDate(n.created_at, locale)}</span>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
